import * as React from 'react';

import { useTranslation } from '@nucleus-commerce/i18n/react';
import { Button } from '@nucleus-commerce/ui/components/button';
import { Spinner } from '@nucleus-commerce/ui/components/spinner';
import { Textarea } from '@nucleus-commerce/ui/components/textarea';

import { ErrorState } from '../errors/error-state';

import type { PickerPasteSkip } from './types';

/** The most values one paste looks up (the lookup endpoints' limit). */
export const PASTE_LIMIT = 5000;

/**
 * Pasted text as lookup values: split on new lines, commas and tabs, trimmed, empty ones and
 * repeats dropped, at most `PASTE_LIMIT`.
 */
export function parsePastedValues(text: string): { values: string[]; truncated: boolean } {
  const unique = [
    ...new Set(
      text
        .split(/[\n\r\t,]+/)
        .map((value) => value.trim())
        .filter(Boolean),
    ),
  ];
  return { values: unique.slice(0, PASTE_LIMIT), truncated: unique.length > PASTE_LIMIT };
}

/** What one Find did: how many ids were new, the misses, and the values left out with why. */
export interface PasteOutcome {
  added: number;
  notFound: string[];
  skipped?: PickerPasteSkip[];
}

export interface ResourcePickerPasteProps {
  /** Looks the values up and adds the found ids; resolves with how many were new. */
  onFind: (values: string[]) => Promise<PasteOutcome>;
  onClose: () => void;
}

/** Skipped values counted per reason, in the order the reasons first appear. */
function countByReason(skipped: readonly PickerPasteSkip[]): Array<[string, number]> {
  const counts = new Map<string, number>();
  for (const { reason } of skipped) counts.set(reason, (counts.get(reason) ?? 0) + 1);
  return [...counts];
}

/**
 * The inline paste box under the picker's search: a textarea, Find, then "Added 48 · 2 not
 * found · 3 already added" with the misses (to copy and fix) and the skipped values listed.
 */
export function ResourcePickerPaste({ onFind, onClose }: ResourcePickerPasteProps) {
  const { t, i18n } = useTranslation('common');
  const textId = React.useId();
  const hintId = React.useId();
  const [text, setText] = React.useState('');
  const [finding, setFinding] = React.useState(false);
  const [error, setError] = React.useState<unknown>(null);
  const [result, setResult] = React.useState<PasteOutcome | null>(null);
  const [copied, setCopied] = React.useState(false);
  const parsed = parsePastedValues(text);
  const number = new Intl.NumberFormat(i18n.language);
  const skipped = result?.skipped ?? [];

  const find = async () => {
    if (parsed.values.length === 0 || finding) return;
    setFinding(true);
    setError(null);
    setCopied(false);
    try {
      setResult(await onFind(parsed.values));
      setText('');
    } catch (caught) {
      setError(caught);
    } finally {
      setFinding(false);
    }
  };

  const copy = async () => {
    if (!result) return;
    try {
      await navigator.clipboard.writeText(result.notFound.join('\n'));
      setCopied(true);
    } catch {
      // No clipboard (insecure context): the list stays selectable by hand.
    }
  };

  return (
    <div className="bg-muted/30 flex flex-col gap-2 border-b px-4 py-3" data-slot="picker-paste">
      <label htmlFor={textId} className="text-sm font-medium">
        {t('resourcePicker.pasteLabel')}
      </label>
      <Textarea
        id={textId}
        value={text}
        onChange={(event) => setText(event.target.value)}
        aria-describedby={hintId}
        rows={3}
        className="max-h-32 font-mono text-xs"
        autoFocus
      />
      <p id={hintId} className="text-muted-foreground text-xs">
        {parsed.truncated
          ? t('resourcePicker.pasteTruncated', { limit: number.format(PASTE_LIMIT) })
          : t('resourcePicker.pasteHint', { limit: number.format(PASTE_LIMIT) })}
      </p>
      {error ? <ErrorState error={error} onRetry={() => void find()} compact /> : null}
      <div role="status" aria-live="polite">
        {result ? (
          <div className="flex flex-col gap-1.5">
            <p className="text-sm">
              {[
                t('resourcePicker.pasteResult', {
                  added: number.format(result.added),
                  notFound: number.format(result.notFound.length),
                }),
                ...countByReason(skipped).map(([reason, count]) =>
                  t('resourcePicker.pasteSkipped', { number: number.format(count), reason }),
                ),
              ].join(' · ')}
            </p>
            {result.notFound.length > 0 ? (
              <div className="flex items-start gap-2">
                <Textarea
                  readOnly
                  value={result.notFound.join('\n')}
                  aria-label={t('resourcePicker.pasteNotFound')}
                  rows={Math.min(result.notFound.length, 4)}
                  className="max-h-24 min-h-0 flex-1 font-mono text-xs"
                />
                <Button type="button" variant="outline" size="sm" onClick={() => void copy()}>
                  {copied ? t('resourcePicker.copied') : t('resourcePicker.copy')}
                </Button>
              </div>
            ) : null}
            {skipped.length > 0 ? (
              <Textarea
                readOnly
                value={skipped.map((skip) => `${skip.value} (${skip.reason})`).join('\n')}
                aria-label={t('resourcePicker.pasteSkippedList')}
                rows={Math.min(skipped.length, 4)}
                className="max-h-24 min-h-0 font-mono text-xs"
              />
            ) : null}
          </div>
        ) : null}
      </div>
      <div className="flex justify-end gap-2">
        <Button type="button" variant="ghost" size="sm" onClick={onClose}>
          {t('resourcePicker.pasteClose')}
        </Button>
        <Button
          type="button"
          size="sm"
          disabled={parsed.values.length === 0 || finding}
          onClick={() => void find()}
        >
          {finding ? <Spinner label={null} /> : null}
          {t('resourcePicker.pasteFind')}
        </Button>
      </div>
    </div>
  );
}
